import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Phone, ArrowRight } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BookingSuccessPage() {
  const [searchParams] = useSearchParams();
  const [paymentStatus, setPaymentStatus] = useState("checking");
  const [attempts, setAttempts] = useState(0);
  const sessionId = searchParams.get("session_id");

  const pollPaymentStatus = useCallback(async () => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setPaymentStatus("timeout");
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`);
      const data = response.data;

      if (data.payment_status === "paid") {
        setPaymentStatus("success");
        return;
      } else if (data.status === "expired") {
        setPaymentStatus("expired");
        return;
      }

      // Continue polling
      setAttempts(prev => prev + 1);
    } catch (error) {
      console.error("Error checking payment status:", error);
      setPaymentStatus("error");
    }
  }, [sessionId, attempts]);

  useEffect(() => {
    if (sessionId && paymentStatus === "checking") {
      const timer = setTimeout(pollPaymentStatus, 2000);
      return () => clearTimeout(timer);
    }
  }, [sessionId, paymentStatus, pollPaymentStatus, attempts]);

  return (
    <div data-testid="booking-success-page" className="bg-[#FAFAF9] min-h-screen pt-32 pb-20">
      <div className="container mx-auto px-4 md:px-8 max-w-2xl">
        <div className="bg-white p-12 rounded-2xl shadow-xl border-t-4 border-[#064E3B] text-center">
          {paymentStatus === "checking" && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#F0FDF4] flex items-center justify-center">
                <div className="w-10 h-10 border-4 border-[#064E3B] border-t-transparent rounded-full animate-spin" />
              </div>
              <h1 className="text-3xl font-bold text-[#0C0A09] mb-4">Processing Payment...</h1>
              <p className="text-[#78716C]">Please wait while we confirm your payment.</p>
            </>
          )}

          {paymentStatus === "success" && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#F0FDF4] flex items-center justify-center">
                <CheckCircle2 className="w-10 h-10 text-[#064E3B]" />
              </div>
              <h1 className="text-3xl font-bold text-[#0C0A09] mb-4">Payment Successful!</h1>
              <p className="text-[#78716C] mb-8">
                Your deposit has been received. We'll contact you shortly to confirm your booking details.
              </p>
              <div className="bg-[#F0FDF4] p-6 rounded-xl mb-8">
                <p className="text-sm text-[#78716C] mb-2">Transaction Reference</p>
                <p className="text-lg font-mono font-bold text-[#064E3B]">{sessionId?.slice(0, 20)}...</p>
              </div>
              <p className="text-[#78716C] text-sm mb-6">
                A confirmation email will be sent to you shortly. If you have any questions, please call us at (647) 870-0445.
              </p>
            </>
          )}

          {(paymentStatus === "error" || paymentStatus === "timeout" || paymentStatus === "expired") && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-100 flex items-center justify-center">
                <span className="text-4xl">⚠️</span>
              </div>
              <h1 className="text-3xl font-bold text-[#0C0A09] mb-4">
                {paymentStatus === "expired" ? "Payment Expired" : "Payment Issue"}
              </h1>
              <p className="text-[#78716C] mb-8">
                {paymentStatus === "expired" 
                  ? "Your payment session has expired. Please try again."
                  : "We couldn't confirm your payment. Please contact us or try again."}
              </p>
            </>
          )}

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/">
              <Button className="bg-[#064E3B] hover:bg-[#065F46] text-white rounded-full px-8 py-6">
                Back to Home
              </Button>
            </Link>
            {paymentStatus !== "success" && (
              <Link to="/booking">
                <Button variant="outline" className="border-[#064E3B] text-[#064E3B] hover:bg-[#064E3B] hover:text-white rounded-full px-8 py-6">
                  Try Again
                </Button>
              </Link>
            )}
            <a href="tel:+16478700445">
              <Button variant="outline" className="border-[#064E3B] text-[#064E3B] hover:bg-[#064E3B] hover:text-white rounded-full px-8 py-6">
                <Phone className="mr-2 w-4 h-4" />
                Call Us
              </Button>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
